const COORDINATE_DECIMAL_PLACES = 10;

const formatDecimal = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return numericValue.toFixed(COORDINATE_DECIMAL_PLACES);
};

const formatIsoDateTime = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  try {
    return new Date(numericValue).toISOString();
  } catch (error) {
    return null;
  }
};

const setQueryValue = (params, key, value) => {
  if (value == null || value === '') {
    params.delete(key);
    return;
  }
  params.set(key, value);
};

const parseDecimal = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const parseCoordinatePair = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parts = raw.split(',').map((part) => parseDecimal(part));
  if (parts.length !== 2 || parts.some((part) => part == null)) return null;
  return { latitude: parts[0], longitude: parts[1] };
};

const normalizeBounds = (bounds) => {
  if (!Array.isArray(bounds) || bounds.length !== 4) return null;
  const [lon1, lat1, lon2, lat2] = bounds;
  if ([lon1, lat1, lon2, lat2].some((entry) => entry == null)) return null;
  return [
    Math.min(lon1, lon2),
    Math.min(lat1, lat2),
    Math.max(lon1, lon2),
    Math.max(lat1, lat2),
  ];
};

const parseFocusBox = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parts = raw.split(',').map((part) => parseDecimal(part));
  if (parts.length !== 4 || parts.some((part) => part == null)) return null;
  return normalizeBounds(parts);
};

const parseGeoBounds = (params) => {
  const minLon = parseDecimal(params.get('geoMinLon'));
  const minLat = parseDecimal(params.get('geoMinLat'));
  const maxLon = parseDecimal(params.get('geoMaxLon'));
  const maxLat = parseDecimal(params.get('geoMaxLat'));

  if ([minLon, minLat, maxLon, maxLat].some((entry) => entry == null)) return null;
  return normalizeBounds([minLon, minLat, maxLon, maxLat]);
};

const parseTimeMs = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

export const parseFilterQueryString = (search = '') => {
  const params = new URLSearchParams(search);
  const forceURL = String(params.get('forceURL') || '').toLowerCase() === 'true';
  const keyword = String(params.get('keyword') ?? params.get('search') ?? '').trim();
  const countryId = String(params.get('countryId') || '').trim();
  const countrySubregion = String(params.get('countrySubregion') || '').trim();
  const geoBoundsSource = String(params.get('geoBoundsSource') || '').trim();

  let latitude = parseDecimal(params.get('latitude'));
  let longitude = parseDecimal(params.get('longitude'));
  const coordinates = parseCoordinatePair(params.get('coordinates'));
  if (latitude == null || longitude == null) {
    latitude = coordinates?.latitude ?? latitude;
    longitude = coordinates?.longitude ?? longitude;
  }

  const zoom = parseDecimal(params.get('zoom'));
  const focusBox = parseFocusBox(params.get('focusBox'));
  const geoFilterBounds = parseGeoBounds(params);
  const timeRangeStart = parseTimeMs(params.get('timeRangeStart'));
  const timeRangeEnd = parseTimeMs(params.get('timeRangeEnd'));

  return {
    forceURL,
    keyword,
    countryId,
    countrySubregion,
    viewport:
      latitude != null && longitude != null
        ? {
            latitude,
            longitude,
            zoom,
          }
        : null,
    focusBox,
    geoFilterBounds,
    geoBoundsSource,
    timeRangeMs: {
      min: timeRangeStart,
      max: timeRangeEnd,
    },
  };
};

export const buildFilterQueryString = ({
  existingSearch = '',
  keyword = '',
  countryId = '',
  countrySubregion = '',
  viewport,
  focusBox,
  geoFilterBounds,
  geoBoundsSource = '',
  timeRangeMs,
}) => {
  const params = new URLSearchParams(existingSearch);
  const isAllZeroBounds = (bounds) => {
    const EPS = 1e-6;
    return (
      Array.isArray(bounds) &&
      bounds.length === 4 &&
      bounds.every((entry) => {
        const value = Number(entry);
        return Number.isFinite(value) && Math.abs(value) <= EPS;
      })
    );
  };

  const trimmedKeyword = String(keyword || '').trim();
  setQueryValue(params, 'keyword', trimmedKeyword);
  setQueryValue(params, 'countryId', String(countryId || '').trim());
  setQueryValue(params, 'countrySubregion', String(countrySubregion || '').trim());

  const latitude = formatDecimal(viewport?.latitude);
  const longitude = formatDecimal(viewport?.longitude);
  const zoomValue = Number(viewport?.zoom);

  setQueryValue(params, 'latitude', latitude);
  setQueryValue(params, 'longitude', longitude);
  setQueryValue(
    params,
    'coordinates',
    latitude && longitude ? `${latitude},${longitude}` : null
  );
  setQueryValue(params, 'zoom', Number.isFinite(zoomValue) ? zoomValue.toFixed(4) : null);

  const focusBoxValue =
    Array.isArray(focusBox) &&
    focusBox.length === 4 &&
    focusBox.every((entry) => Number.isFinite(Number(entry))) &&
    !isAllZeroBounds(focusBox)
      ? focusBox.map((entry) => formatDecimal(entry)).join(',')
      : null;
  setQueryValue(params, 'focusBox', focusBoxValue);

  const hasGeoBounds =
    Array.isArray(geoFilterBounds) &&
    geoFilterBounds.length === 4 &&
    geoFilterBounds.every((entry) => Number.isFinite(Number(entry))) &&
    !isAllZeroBounds(geoFilterBounds);
  const [geoMinLon, geoMinLat, geoMaxLon, geoMaxLat] = hasGeoBounds
    ? geoFilterBounds
    : [null, null, null, null];

  setQueryValue(params, 'geoMinLon', formatDecimal(geoMinLon));
  setQueryValue(params, 'geoMinLat', formatDecimal(geoMinLat));
  setQueryValue(params, 'geoMaxLon', formatDecimal(geoMaxLon));
  setQueryValue(params, 'geoMaxLat', formatDecimal(geoMaxLat));
  setQueryValue(params, 'geoBoundsSource', String(geoBoundsSource || '').trim());

  setQueryValue(params, 'timeRangeStart', formatIsoDateTime(timeRangeMs?.min));
  setQueryValue(params, 'timeRangeEnd', formatIsoDateTime(timeRangeMs?.max));

  return params.toString();
};
